import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Business } from '../../contexts/BusinessContext';

interface BookingData {
  id: string;
  booking_number: number;
  title: string;
  start_date: string;
  start_time: string;
  customer: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export default function BookingSuccess() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking');
  const [business, setBusiness] = useState<Business | null>(null);
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadBusinessAndBooking() {
      if (!businessSlug) {
        setError('Business not found');
        setLoading(false);
        return;
      }

      if (!supabase) {
        console.error('Supabase client is not initialized');
        setError('Unable to connect to the database');
        setLoading(false);
        return;
      }

      try {
        // Load business data
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('*')
          .eq('slug', businessSlug)
          .single();

        if (businessError || !businessData) {
          console.error('Error fetching business:', businessError);
          setError('Business not found');
          setLoading(false);
          return;
        }

        setBusiness(businessData);

        // Load booking data if booking ID is provided
        if (bookingId) {
          const { data: bookingData, error: bookingError } = await supabase
            .from('bookings')
            .select(`
              id,
              booking_number,
              title,
              start_date,
              start_time,
              customers:customer_id (
                first_name,
                last_name,
                email
              )
            `)
            .eq('id', bookingId)
            .eq('business_id', businessData.id)
            .single();

          if (bookingError) {
            console.error('Error fetching booking:', bookingError);
            // Don't set error here, just log it - business info is more important
          } else if (bookingData) {
            // Handle customers data - it comes as an array from the relation
            const customerData = Array.isArray(bookingData.customers) 
              ? bookingData.customers[0] 
              : bookingData.customers;
              
            setBooking({
              id: bookingData.id,
              booking_number: bookingData.booking_number,
              title: bookingData.title,
              start_date: bookingData.start_date,
              start_time: bookingData.start_time,
              customer: {
                first_name: (customerData as any)?.first_name || '',
                last_name: (customerData as any)?.last_name || '',
                email: (customerData as any)?.email || ''
              }
            });
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load booking information');
      } finally {
        setLoading(false);
      }
    }

    loadBusinessAndBooking();
  }, [businessSlug, bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Error</h1>
          <p className="text-gray-700">{error || 'Business not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top bar */}
      <div 
        className="h-24 w-full flex items-center justify-start px-6 sm:px-8" 
        style={{ backgroundColor: business?.booking_settings?.navbar_color || '#000000' }}
      >
        {business?.booking_settings?.logo_url && (
          <img 
            src={business.booking_settings.logo_url} 
            alt={`${business.name} logo`}
            className="h-16 max-w-xs object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
          
          <p className="text-gray-600 mb-6">
            Thank you for your booking with {business.name}. We've received your request and will see you at your scheduled appointment time.
          </p>
          
          {booking && (
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <p className="text-sm text-gray-500">Booking Reference</p>
              <p className="font-medium">#{booking.booking_number}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              A confirmation email has been sent to your email address with all the details of your booking.
            </p>
            
            <div className="pt-4">
              <Link
                to={`/${businessSlug}/book`}
                className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                Book Another Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 